import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CollapseChevron } from "components/CollapseChevron";
import { useState } from "react";

import { ActionsRow, DeadlinePill, EpaBody, HeaderRow } from "./ComplianceCard";
import {
  type ComplianceItem,
  type ComplianceItemEpa,
  sortByDeadlineAscending,
} from "./CompliancePane.helpers";
import { COLUMN_HEADER_SX } from "./DiscrepancyReviewDialog.helpers";
import { DiscrepancyStatusChip } from "./DiscrepancyStatusChip";
import { discrepancyTableSx, EXPANDER_INSET_NO_SELECTION } from "./discrepancyTableSx";

const COL_COUNT = 5; // chevron, Request, Detail, Received, Status

function getRequestText(item: ComplianceItemEpa): string {
  if (item.sections.length === 0) return "EPA-requested correction";
  return item.sections.map((s) => s.sectionNumber).join(", ");
}

function getDetailText(item: ComplianceItemEpa): string {
  const comments = item.sections.map((s) => s.comments).filter((c) => c.length > 0);
  return comments.length > 0 ? comments.join(" · ") : "—";
}

export interface EpaCorrectionsPaneProps {
  items: ComplianceItem[];
  now: Date;
  loading?: boolean;
  activePreviewDocId?: string | null;
  onViewDocument: (documentId: string, fileExtension: string | null) => void;
  onResolveInEManifest: (discrepancyId: string) => void;
}

function EpaCorrectionsLoadingSkeleton() {
  return (
    <Stack spacing={2} sx={{ pb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {["", "Request", "Detail", "Received", "Status"].map((h, i) => (
              <TableCell key={h || `sk-${i}`}>
                <Skeleton variant="text" width={80} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 2 }, (_, i) => (
            <TableRow key={i}>
              {Array.from({ length: COL_COUNT }, (__, j) => (
                <TableCell key={j}>
                  <Skeleton variant="text" width={j === 2 ? 160 : 80} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

function EpaCorrectionsHeader() {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" />
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 200 }}>Request</TableCell>
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 240 }}>Detail</TableCell>
        <TableCell sx={{ ...COLUMN_HEADER_SX, minWidth: 120 }}>Received</TableCell>
        <TableCell data-status-cell sx={{ ...COLUMN_HEADER_SX, minWidth: 100 }}>
          Status
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

function ChevronCell({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <TableCell data-icon-cell="chevron">
      <Box data-row-icon>
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={open ? "Collapse row" : "Expand row"}
        >
          <CollapseChevron collapsed={!open} />
        </IconButton>
      </Box>
    </TableCell>
  );
}

function EpaCorrectionRow({
  item,
  now,
  open,
  onToggleOpen,
  activePreviewDocId,
  onViewDocument,
  onResolveInEManifest,
}: {
  item: ComplianceItemEpa;
  now: Date;
  open: boolean;
  onToggleOpen: () => void;
} & Pick<
  EpaCorrectionsPaneProps,
  "activePreviewDocId" | "onViewDocument" | "onResolveInEManifest"
>) {
  const isPreviewedDoc =
    activePreviewDocId != null &&
    item.sourceDocumentId != null &&
    activePreviewDocId === item.sourceDocumentId;

  return (
    <>
      <TableRow hover data-expanded={open ? "true" : undefined}>
        <ChevronCell open={open} onToggle={onToggleOpen} />
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2">{getRequestText(item)}</Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 320, wordBreak: "break-word" }}>
          <Typography variant="body2">{getDetailText(item)}</Typography>
        </TableCell>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <Typography variant="body2">{item.requestedAt}</Typography>
        </TableCell>
        <TableCell data-status-cell>
          <DiscrepancyStatusChip status={item.status} />
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          {/* One column short of the row, so the deadline lands in the Status
              column it belongs under rather than at the table's right edge. */}
          <TableCell
            colSpan={COL_COUNT - 1}
            sx={{
              pt: 1.5,
              pb: 2,
              pr: 2,
              // Lines up with the row's first text column; no checkbox
              // column here, so the inset is the chevron cell alone.
              pl: EXPANDER_INSET_NO_SELECTION,
              "& > * + *": { mt: 1 },
            }}
          >
            <HeaderRow item={item} />
            <EpaBody item={item} />
            <Box>
              <ActionsRow
                item={item}
                isPreviewedDoc={isPreviewedDoc}
                onViewDocument={onViewDocument}
                onResolveInEManifest={onResolveInEManifest}
              />
            </Box>
          </TableCell>
          <TableCell data-status-cell sx={{ pt: 1.5, whiteSpace: "nowrap" }}>
            <DeadlinePill item={item} now={now} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function EpaCorrectionsEmptyRow() {
  return (
    <TableRow>
      <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 8, borderBottom: "none" }}>
        <Typography variant="body2" color="text.secondary">
          No EPA-requested corrections found for the selected manifest(s).
        </Typography>
      </TableCell>
    </TableRow>
  );
}

/**
 * EPA-Requested Corrections as a four-column table (Request, Detail, Received,
 * Status), one row per finding. Everything the old card showed lives in the
 * row's expander, opened with the leading chevron.
 */
export function EpaCorrectionsPane({
  items,
  now,
  loading = false,
  activePreviewDocId,
  onViewDocument,
  onResolveInEManifest,
}: EpaCorrectionsPaneProps) {
  const theme = useTheme();

  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const toggleOpen = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (loading && items.length === 0) {
    return <EpaCorrectionsLoadingSkeleton />;
  }

  const sortedItems = sortByDeadlineAscending(items, now);

  return (
    <TableContainer
      sx={{
        overflow: "visible",
        bgcolor: "background.paper",
        borderBottomLeftRadius: "inherit",
        borderBottomRightRadius: "inherit",
      }}
    >
      <Table
        size="small"
        sx={{
          ...discrepancyTableSx(theme),
        }}
      >
        <EpaCorrectionsHeader />
        <TableBody>
          {sortedItems.map((item) =>
            item.kind === "epaCorrection" ? (
              <EpaCorrectionRow
                key={item.id}
                item={item}
                now={now}
                open={openIds.has(item.id)}
                onToggleOpen={() => toggleOpen(item.id)}
                activePreviewDocId={activePreviewDocId}
                onViewDocument={onViewDocument}
                onResolveInEManifest={onResolveInEManifest}
              />
            ) : null
          )}
          {sortedItems.length === 0 && <EpaCorrectionsEmptyRow />}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
